import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import Field from '../../../../../../components/Form/Field'

import {
  calculateBudgetFromImpressions,
  calculateImpressionsFromBudget
} from '../../../../UploadAdCreative/ImpressionsBudgetSection/helpers'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { formatNumber } from '../../../../../../helpers/numbers'

import {
  clearUpdateTikTokLineItemBudget,
  updateTikTokLineItemBudget
} from '../../../../../../modules/actions/lineItems'
import {
  updateTikTokLineItemBudgetErrorSelector,
  updateTikTokLineItemBudgetIsLoadingSelector,
  updateTikTokLineItemBudgetWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { selectedTikTokAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import { rightSelector } from '../../../../../../modules/selectors/rights'

import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { IMPRESSIONS } from '../../../../UploadAdCreative/fields'
import { getImpressionsValidationsSchema } from '../../../../../ReusableFormSections/reusableBudgetSections/remainingImpressionsValidation'
import { BUDGET } from '../../../../../Tiktok/LineItemForms/LineItemTikTokEdit/fields'

const TikTokLineItemImpressionsBudgetForm = ({
  lineItem,
  providedAdAccountId,
  remainingImpressions,
  title,
  ...formProps
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { bid_cap: rightBidCap } = useSelector(rightSelector)
  const { id: selectedTiktokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)
  const tikTokLineItemBudgetWasUpdated = useSelector(updateTikTokLineItemBudgetWasUpdatedSelector)

  const adAccountId = providedAdAccountId || selectedTiktokAdAccountId
  const { [BUDGET]: lineItemBudget } = lineItem

  const handleSubmit = useCallback(
    values => {
      dispatch(
        updateTikTokLineItemBudget({
          account: adAccountId,
          [BUDGET]: [
            {
              [BUDGET]: calculateBudgetFromImpressions({
                impressions: values[IMPRESSIONS],
                rightBidCap
              }),
              adgroup_id: lineItem.id
            }
          ],
          meta: {
            [HISTORY_META_EDIT_SECTION]: title
          }
        })
      )
    },
    [dispatch, lineItem, adAccountId, title, rightBidCap]
  )

  const initialLineItemImpressions = useMemo(() => {
    return calculateImpressionsFromBudget({
      budget: lineItemBudget,
      rightBidCap
    })
  }, [lineItemBudget, rightBidCap])

  const initialValues = useMemo(() => {
    return {
      [IMPRESSIONS]: initialLineItemImpressions
    }
  }, [initialLineItemImpressions])

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({
    initialValues,
    handleSubmit
  })

  const successSubmit = tikTokLineItemBudgetWasUpdated || manualSuccessSubmit

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema: getImpressionsValidationsSchema(remainingImpressions, initialLineItemImpressions),
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateTikTokLineItemBudget())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      errorSelector={updateTikTokLineItemBudgetErrorSelector}
      isLoadingSelector={updateTikTokLineItemBudgetIsLoadingSelector}
      successSubmit={successSubmit}
      clearEditItem={handleClearUpdateLineItem}
      {...formProps}
    >
      <p>{t(`Your remaining total is ${formatNumber(remainingImpressions)} impressions`)}</p>
      <Field
        type="number"
        formik={formik}
        name={IMPRESSIONS}
        placeholder={t('Lifetime Impressions')}
        autoComplete="off"
        inputMode="numeric"
      />
    </EditForm>
  )
}

export default TikTokLineItemImpressionsBudgetForm
